// FdocApi.js - API utilities for Tax and FDOC (Document Management) operations
import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

// API uses the Innoworx API endpoint
// Base URL as per API documentation
const API_BASE_URL = 'https://api.innoworx.ai/Stage';
const FDOC_ORDER_HISTORY_BASE_URL = 'https://x1f3lmpgxa.execute-api.ap-south-1.amazonaws.com/Stage';

const appendOptionalQueryParam = (queryParams, key, value) => {
  if (value !== undefined && value !== null && value !== '') {
    queryParams.push(`${key}=${encodeURIComponent(value)}`);
  }
};

/**
 * Helper function to make authenticated API requests
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null, baseUrl = API_BASE_URL) => {
  try {
    // Get authentication token
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    if (!idToken) {
      throw new Error('No authentication token available');
    }

    // Normalize token to avoid invalid header values (e.g. hidden newlines)
    // Browsers reject headers with certain characters and show a hashed value in console.
    const token = String(idToken).trim().replace(/\s+/g, '');

    // Validate token format (should be a JWT)
    const jwtLike = /^[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+$/.test(token);
    if (!token || token.length < 10 || !jwtLike) {
      throw new Error('Invalid authentication token format');
    }

    // Ensure we always hit the CloudFront-backed absolute URL.
    // If something passes a relative base (e.g. "/Stage"), browsers will call localhost:3000/Stage/... in dev.
    const resolvedBaseUrl =
      typeof baseUrl === 'string' && /^https?:\/\//i.test(baseUrl) ? baseUrl : API_BASE_URL;
    const url = `${resolvedBaseUrl}${endpoint}`;
    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    };

    if (body && method !== 'GET') {
      options.body = JSON.stringify(body);
    }

    console.log(`API: Making ${method} request to: ${url}`);
    console.log('API: Authorization header format:', `Bearer ${token.substring(0, 20)}...`);
    if (body) {
      console.log('API: Request payload:', body);
    }

    let response;
    try {
      response = await fetch(url, options);
    } catch (networkError) {
      console.error('API: Network error:', networkError);
      throw new Error(`Network error: ${networkError.message}`);
    }

    // Parse response (allow 204 No Content / empty body)
    let responseData;
    const text = await response.text();
    if (text && text.trim()) {
      try {
        responseData = JSON.parse(text);
      } catch (parseError) {
        console.error('API: Error parsing response:', parseError);
        throw new Error('Invalid JSON response from server');
      }
    } else {
      responseData = {};
    }

    if (!response.ok) {
      console.error(
        `API Error: ${response.status} ${response.statusText}`,
        responseData
      );
      const error = new Error(
        (responseData && (responseData.message || responseData.error)) ||
          `API Error: ${response.status} - ${response.statusText}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = responseData;

        // Log API error to logging service
        loggingService.logError(
          'taxFdocApiError',
          `Tax/FDOC API error: ${response.status} ${response.statusText} for ${endpoint}`,
          error,
          {
            endpoint,
            method,
            status: response.status,
            statusText: response.statusText,
            responseData: responseData,
          }
        ).catch(logError => {
          console.warn('[FdocApi] Failed to log error:', logError);
        });
        
      throw error;
    }

    console.log('API: Response status:', response.status);
    console.log('API: Response data:', responseData);

    return responseData;
  } catch (error) {
    // Log network/fetch errors to logging service
    if (error.status === undefined) {
    console.error('API: Error in makeApiRequest:', error);
    error.endpoint = endpoint;
    error.method = method;
    
    loggingService.logError(
      'taxFdocApiError',
      `Tax/FDOC API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[FdocApi] Failed to log error:', logError);
    });
  }

    throw error;
  }
};

/**
 * API utility functions for Tax Management
 */
export const taxApi = {
  /**
   * Get tax overview across all document types (Quotations, Purchase Orders, Invoices, Bills)
   * GET /tax/overview
   * @param {Object} [params={}] - Query parameters
   * @param {number} [params.month] - Month (1-12) - required if year is provided
   * @param {number} [params.year] - Year (YYYY, 2000-2100) - required if month is provided
   * @param {string} [params.document_type] - Filter by type: 'quotation', 'po', 'invoice', 'bill', or 'all' (default)
   * @param {number} [params.limit] - Page size (default: 200, max: 500)
   * @param {string} [params.last_key] - JSON-encoded DynamoDB key for pagination
   * @returns {Promise<Object>} - Response object containing:
   *   - documents: Array of tax document objects
   *   - summary: Summary object with totals and breakdown by type
   *   - count: Total number of documents returned
   *   - last_key: Key for next page (null if no more pages)
   * @throws {Error} - Throws an error if the API request fails
   * 
   * @example
   * // Get all tax documents
   * const result = await taxApi.getOverview();
   * 
   * @example
   * // Get tax documents for November 2025
   * const result = await taxApi.getOverview({ month: 11, year: 2025 });
   * 
   * @example
   * // Get only invoices for November 2025
   * const result = await taxApi.getOverview({ 
   *   month: 11, 
   *   year: 2025, 
   *   document_type: 'invoice' 
   * });
   * 
   * @example
   * // Get all bills with pagination
   * const result = await taxApi.getOverview({ 
   *   document_type: 'bill', 
   *   limit: 100, 
   *   last_key: '...' 
   * });
   */
  getOverview: async (params = {}) => {
    try {
      console.log('Tax API: Fetching tax overview', params);

      // Build query parameters
      const queryParams = [];

      // Validate month and year if provided
      if (params.month !== undefined) {
        if (params.year === undefined) {
          throw new Error('year is required when month is provided');
        }
        if (params.month < 1 || params.month > 12) {
          throw new Error('month must be between 1 and 12');
        }
        queryParams.push(`month=${params.month}`);
      }

      if (params.year !== undefined) {
        if (params.month === undefined) {
          throw new Error('month is required when year is provided');
        }
        if (params.year < 2000 || params.year > 2100) {
          throw new Error('year must be between 2000 and 2100');
        }
        queryParams.push(`year=${params.year}`);
      }

      if (params.document_type) {
        const validTypes = ['quotation', 'po', 'invoice', 'bill', 'all'];
        if (!validTypes.includes(params.document_type)) {
          throw new Error(
            `document_type must be one of: ${validTypes.join(', ')}`
          );
        }
        queryParams.push(`document_type=${encodeURIComponent(params.document_type)}`);
      }

      if (params.limit !== undefined) {
        if (params.limit < 1 || params.limit > 500) {
          throw new Error('limit must be between 1 and 500');
        }
        queryParams.push(`limit=${params.limit}`);
      }

      if (params.last_key) {
        queryParams.push(`last_key=${encodeURIComponent(params.last_key)}`);
      }

      // Build endpoint with query parameters
      let endpoint = '/tax/overview';
      if (queryParams.length > 0) {
        endpoint += `?${queryParams.join('&')}`;
      }

      const responseData = await makeApiRequest(endpoint);

      // Validate response structure
      if (responseData && typeof responseData === 'object') {
        // API returns 'items', but we also support 'documents' for backward compatibility
        const items = responseData.items || responseData.documents || [];

           // Log API event for successful fetch
           loggingService.logInfo(
            'api_success_tax',
            `Successfully fetched tax overview`,
            {
              endpoint: '/tax/overview',
              method: 'GET',
              documentType: params.document_type || 'all',
              count: responseData.count || 0,
              month: params.month,
              year: params.year,
            }
          ).catch(logError => {
            console.warn('[FdocApi] Failed to log success event:', logError);
          });
        
        return {
          items: Array.isArray(items) ? items : [],
          documents: Array.isArray(items) ? items : [], // Keep for backward compatibility
          summary: responseData.summary || {
            total_documents: 0,
            total_amount: 0,
            total_tax: 0,
            by_type: {},
          },
          count: responseData.count || 0,
          last_key: responseData.last_key || responseData.Last_key || null,
        };
      } else {
        throw new Error('Invalid response format from Tax API');
      }
    } catch (error) {
      console.error('Tax API: Error in getOverview:', error);

      // Handle specific error cases based on status codes
      if (error.status === 400) {
        throw new Error(
          `Bad Request: ${error.responseData?.message || error.message}`
        );
      } else if (error.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (error.status === 500) {
        throw new Error(
          `Server error: ${error.responseData?.error || 'Unknown server error'}`
        );
      } else {
        throw new Error(`Error fetching tax overview: ${error.message}`);
      }
    }
  },
};

/**
 * API utility functions for FDOC (Document Management)
 */
export const fdocApi = {
  /**
   * Generate a presigned POST URL for uploading a document file to S3
   * POST /fdoc/upload/presign
   * @param {Object} [params={}] - Upload parameters
   * @param {string} [params.file_name] - Filename (default: "document.pdf")
   * @param {string} [params.content_type] - File content type (default: "application/pdf")
   * @returns {Promise<Object>} - Response object containing:
   *   - fdoc_id: Unique document identifier (UUID)
   *   - bucket: S3 bucket name for temporary storage
   *   - object_key: S3 object key for the uploaded file
   *   - content_type: File content type
   *   - presigned_post: Presigned POST URL and fields for direct S3 upload
   *   - expires_in: Presigned URL expiration time in seconds
   *   - expires_at: Document expiration timestamp
   * @throws {Error} - Throws an error if the API request fails
   * 
   * @example
   * // Presign upload for a PDF
   * const result = await fdocApi.presignUpload({
   *   file_name: 'invoice-2025-001.pdf',
   *   content_type: 'application/pdf'
   * });
   */
  presignUpload: async (params = {}) => {
    try {
      console.log('FDOC API: Presigning upload', params);

      const requestBody = {};
      if (params.file_name) {
        requestBody.file_name = params.file_name;
      }
      if (params.content_type) {
        requestBody.content_type = params.content_type;
      }
      if (params.site_id) {
        requestBody.site_id = params.site_id;
      }
      if (params.document_type) {
        requestBody.document_type = params.document_type;
      }

      const responseData = await makeApiRequest(
        '/fdoc/upload/presign',
        'POST',
        requestBody
      );

       // Log API event for successful presign
       loggingService.logInfo(
        'api_success_fdoc',
        `Successfully generated presigned upload URL`,
        {
          endpoint: '/fdoc/upload/presign',
          method: 'POST',
          fdocId: responseData.fdoc_id,
          contentType: params.content_type || 'application/pdf',
          fileName: params.file_name,
        }
      ).catch(logError => {
        console.warn('[FdocApi] Failed to log success event:', logError);
      });

      return {
        fdoc_id: responseData.fdoc_id,
        bucket: responseData.bucket,
        object_key: responseData.object_key,
        content_type: responseData.content_type,
        presigned_post: responseData.presigned_post,
        expires_in: responseData.expires_in,
        expires_at: responseData.expires_at,
      };
    } catch (error) {
      console.error('FDOC API: Error in presignUpload:', error);

      if (error.status === 400) {
        throw new Error(
          `Bad Request: ${error.responseData?.message || error.message}`
        );
      } else if (error.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (error.status === 500) {
        throw new Error(
          `Server error: ${error.responseData?.error || 'Unknown server error'}`
        );
      } else {
        throw new Error(`Error presigning upload: ${error.message}`);
      }
    }
  },

  /**
   * Extract data from an uploaded document using AWS Textract (PDF) or pandas (Excel)
   * POST /fdoc/extract
   * @param {Object} params - Extract parameters
   * @param {string} params.fdoc_id - Document identifier from presign response
   * @param {string} params.s3_key - S3 object key (from presign response)
   * @returns {Promise<Object>} - Response object containing:
   *   - fdoc_id: Document identifier
   *   - status: Document status (extracted or extraction_failed)
   *   - extracted_data: Extracted data object with type, date, and fields
   *   - extraction_metadata: Metadata about the extraction process
   * @throws {Error} - Throws an error if the API request fails
   * 
   * @example
   * // Extract data from uploaded document
   * const result = await fdocApi.extract({
   *   fdoc_id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
   *   s3_key: 'temp/tenant-123/.../invoice.pdf'
   * });
   */
  extract: async (params) => {
    try {
      if (!params.fdoc_id) {
        throw new Error('fdoc_id is required');
      }
      if (!params.s3_key) {
        throw new Error('s3_key is required');
      }

      console.log('FDOC API: Extracting document data', {
        fdoc_id: params.fdoc_id,
        s3_key: params.s3_key,
      });

      const responseData = await makeApiRequest(
        '/fdoc/extract',
        'POST',
        {
          fdoc_id: params.fdoc_id,
          s3_key: params.s3_key,
        }
      );

         // Log API event for successful extraction
         loggingService.logInfo(
          'api_success_fdoc',
          `Successfully extracted document data`,
          {
            endpoint: '/fdoc/extract',
            method: 'POST',
            fdocId: params.fdoc_id,
            status: responseData.status,
          }
        ).catch(logError => {
          console.warn('[FdocApi] Failed to log success event:', logError);
        });

      return {
        fdoc_id: responseData.fdoc_id,
        status: responseData.status,
        extracted_data: responseData.extracted_data,
        extraction_metadata: responseData.extraction_metadata,
      };
    } catch (error) {
      // Only log detailed error in development or if it's not a known user-friendly error
      const errorMessage = error.responseData?.error || error.responseData?.message || error.message || '';
      const isKnownError = errorMessage.includes('extraction_failed') || 
                          errorMessage.includes('UnsupportedDocumentException') || 
                          errorMessage.includes('unsupported document format');
      
      if (!isKnownError) {
        console.error('FDOC API: Error in extract:', error);
      } else {
        // Log a brief message for known errors
        console.warn('FDOC API: Document extraction issue:', errorMessage.substring(0, 100));
      }

      if (error.status === 400) {
        const errorMessage = error.responseData?.message || error.responseData?.error || error.message || 'Bad Request';
        
        // Provide user-friendly messages for common 400 errors
        if (errorMessage.includes('extraction_failed') && errorMessage.includes('expected')) {
          throw new Error(
            'This document has already failed extraction. Please re-upload the document to try again.'
          );
        } else if (errorMessage.includes('status') && errorMessage.includes('expected')) {
          throw new Error(
            'This document cannot be extracted in its current state. Please re-upload the document.'
          );
        } else {
          throw new Error(
            `Bad Request: ${errorMessage}`
          );
        }
      } else if (error.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (error.status === 404) {
        throw new Error('Document not found.');
      } else if (error.status === 500) {
        // Check for specific error types and provide user-friendly messages
        const errorMessage = error.responseData?.error || error.message || 'Unknown server error';
        
        if (errorMessage.includes('UnsupportedDocumentException') || errorMessage.includes('unsupported document format')) {
          // Check if it's a PDF that failed
          const isPdfError = errorMessage.toLowerCase().includes('pdf') || 
                            errorMessage.toLowerCase().includes('document');
          
          if (isPdfError) {
            throw new Error(
              'This PDF file cannot be processed. The PDF may be corrupted, password-protected, or in an unsupported format. Please try:\n' +
              '• Converting the PDF to a new file\n' +
              '• Removing password protection if present\n' +
              '• Uploading as a PNG or JPEG image instead'
            );
          } else {
            throw new Error(
              'This document format is not supported. Please upload a PDF, PNG, or JPEG image file.'
            );
          }
        } else if (errorMessage.includes('extraction_failed')) {
          throw new Error(
            'Document extraction failed. The document may be corrupted or in an unsupported format. Please try uploading again.'
          );
        } else {
          throw new Error(
            `Server error: ${errorMessage}`
          );
        }
      } else {
        throw new Error(`Error extracting document: ${error.message}`);
      }
    }
  },

  /**
   * Retrieve a document record including metadata and extracted data
   * GET /fdoc/{fdoc_id}?include_download_url=true
   * @param {string} fdoc_id - Document identifier
   * @returns {Promise<Object>} - Response object containing complete document record (with download_url when requested)
   * @throws {Error} - Throws an error if the API request fails
   * 
   * @example
   * // Get document by ID
   * const document = await fdocApi.getDocument('a1b2c3d4-e5f6-7890-abcd-ef1234567890');
   */
  getDocument: async (fdoc_id) => {
    try {
      if (!fdoc_id) {
        throw new Error('fdoc_id is required');
      }

      console.log('FDOC API: Getting document', { fdoc_id });

      const responseData = await makeApiRequest(`/fdoc/${fdoc_id}?include_download_url=true`, 'GET');

      // Handle file_missing when API returns it in body (e.g. 200 with body.error or statusCode 410)
      const body = responseData?.body || responseData;
      const isFileMissingInBody =
        responseData?.statusCode === 410 ||
        responseData?.error === 'file_missing' ||
        body?.error === 'file_missing';
      if (responseData && isFileMissingInBody) {
        const msg =
          body?.message ||
          responseData?.message ||
          'Source file is no longer available in storage. Please re-upload the document.';
        const e = new Error(msg);
        e.status = 410;
        e.code = 'file_missing';
        e.fileMissing = true;
        e.responseData = responseData;
        throw e;
      }

       // Log API event for successful fetch
       loggingService.logInfo(
        'api_success_fdoc',
        `Successfully fetched document`,
        {
          endpoint: `/fdoc/${fdoc_id}`,
          method: 'GET',
          fdocId: fdoc_id,
          status: responseData.status,
        }
      ).catch(logError => {
        console.warn('[FdocApi] Failed to log success event:', logError);
      });

      return responseData;
    } catch (error) {
      console.error('FDOC API: Error in getDocument:', error);

      // 410 Gone: source file no longer in storage (broken link)
      if (error.status === 410) {
        const body = error.responseData?.body || error.responseData;
        const msg = body?.message || error.responseData?.message || 'Source file is no longer available in storage. Please re-upload the document.';
        const e = new Error(msg);
        e.status = 410;
        e.code = 'file_missing';
        e.fileMissing = true;
        e.responseData = error.responseData;
        throw e;
      }
      if (error.status === 400) {
        throw new Error(
          `Bad Request: ${error.responseData?.message || error.message}`
        );
      } else if (error.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (error.status === 404) {
        throw new Error('Document not found.');
      } else if (error.status === 500) {
        throw new Error(
          `Server error: ${error.responseData?.error || 'Unknown server error'}`
        );
      } else {
        throw new Error(`Error getting document: ${error.message}`);
      }
    }
  },

  /**
   * Save confirmed/edited extracted data after user review. Moves file from temporary to permanent storage.
   * POST /fdoc/{fdoc_id}/confirm
   * @param {Object} params - Confirm parameters
   * @param {string} params.fdoc_id - Document identifier
   * @param {Object} params.extracted_data - Confirmed/edited extracted data
   * @returns {Promise<Object>} - Response object containing:
   *   - fdoc_id: Document identifier
   *   - status: Document status (set to confirmed)
   *   - confirmed_at: Confirmation timestamp
   *   - s3_key_permanent: S3 key in permanent bucket
   * @throws {Error} - Throws an error if the API request fails
   * 
   * @example
   * // Confirm document with edited data
   * const result = await fdocApi.confirm({
   *   fdoc_id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
   *   extracted_data: {
   *     type: 'INVOICE',
   *     date: '2025-01-15',
   *     fields: { ... }
   *   }
   * });
   */
  confirm: async (params) => {
    try {
      if (!params.fdoc_id) {
        throw new Error('fdoc_id is required');
      }
      if (!params.extracted_data) {
        throw new Error('extracted_data is required');
      }

      console.log('FDOC API: Confirming document', {
        fdoc_id: params.fdoc_id,
        extracted_data: params.extracted_data,
        apply_authorised_esign: params.apply_authorised_esign,
        signatory_id: params.signatory_id,
      });

      const requestBody = {
        extracted_data: params.extracted_data,
      };

      if (params.apply_authorised_esign === true) {
        requestBody.apply_authorised_esign = true;
        if (params.signatory_id) {
          requestBody.signatory_id = params.signatory_id;
        }
      } else if (params.apply_authorised_esign === false) {
        // Explicitly allow callers to turn it off
        requestBody.apply_authorised_esign = false;
      }

      const responseData = await makeApiRequest(
        `/fdoc/${params.fdoc_id}/confirm`,
        'POST',
        requestBody
      );

         // Log API event for successful confirmation
         loggingService.logInfo(
          'api_success_fdoc',
          `Successfully confirmed document`,
          {
            endpoint: `/fdoc/${params.fdoc_id}/confirm`,
            method: 'POST',
            fdocId: params.fdoc_id,
            status: responseData.status,
          }
        ).catch(logError => {
          console.warn('[FdocApi] Failed to log success event:', logError);
        });

      return {
        fdoc_id: responseData.fdoc_id,
        status: responseData.status,
        version_no: responseData.version_no,
        confirmed_at: responseData.confirmed_at,
        s3_key_permanent: responseData.s3_key_permanent,
        esign_applied: responseData.esign_applied,
        esign_signatory_id: responseData.esign_signatory_id,
        esign_signatory_name: responseData.esign_signatory_name,
        esign_signatory_designation: responseData.esign_signatory_designation,
      };
    } catch (error) {
      console.error('FDOC API: Error in confirm:', error);

      if (error.status === 400) {
        throw new Error(
          `Bad Request: ${error.responseData?.message || error.message}`
        );
      } else if (error.status === 409) {
        const backendCode = error.responseData?.error || error.responseData?.code;
        const e = new Error(
          backendCode === 'version_conflict'
            ? 'This document was updated elsewhere. Please refresh and try again.'
            : (error.responseData?.message || error.message || 'Conflict')
        );
        e.status = 409;
        e.code = backendCode || 'conflict';
        e.responseData = error.responseData;
        throw e;
      } else if (error.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (error.status === 404) {
        throw new Error('Document not found.');
      } else if (error.status === 500) {
        throw new Error(
          `Server error: ${error.responseData?.error || 'Unknown server error'}`
        );
      } else {
        throw new Error(`Error confirming document: ${error.message}`);
      }
    }
  },

  /**
   * List confirmed versions (immutable snapshots) for a document.
   * GET /fdoc/{fdoc_id}/versions
   * Pagination: last_key is opaque; may be object or null.
   */
  listVersions: async (fdoc_id, params = {}) => {
    try {
      if (!fdoc_id) throw new Error('fdoc_id is required');

      const limit = params.limit ?? 50;
      if (limit < 1 || limit > 500) throw new Error('limit must be between 1 and 500');

      const queryParams = [`limit=${encodeURIComponent(String(limit))}`];
      if (params.last_key !== undefined && params.last_key !== null && params.last_key !== '') {
        const token =
          typeof params.last_key === 'string' ? params.last_key : JSON.stringify(params.last_key);
        queryParams.push(`last_key=${encodeURIComponent(token)}`);
      }

      const endpoint = `/fdoc/${fdoc_id}/versions?${queryParams.join('&')}`;
      const responseData = await makeApiRequest(endpoint, 'GET');

      return {
        items: Array.isArray(responseData?.items) ? responseData.items : [],
        count: responseData?.count ?? 0,
        last_key: responseData?.last_key ?? null,
      };
    } catch (error) {
      throw new Error(`Error listing versions: ${error.message}`);
    }
  },

  /**
   * Get a specific version snapshot (read-only).
   * GET /fdoc/{fdoc_id}/versions/{version_no}
   */
  getVersionSnapshot: async (fdoc_id, version_no) => {
    try {
      if (!fdoc_id) throw new Error('fdoc_id is required');
      if (version_no === undefined || version_no === null || version_no === '') {
        throw new Error('version_no is required');
      }

      const endpoint = `/fdoc/${fdoc_id}/versions/${encodeURIComponent(String(version_no))}`;
      const responseData = await makeApiRequest(endpoint, 'GET');
      return responseData;
    } catch (error) {
      throw new Error(`Error getting version snapshot: ${error.message}`);
    }
  },

  /**
   * Cancel (soft-delete) an FDOC document.
   * POST /fdoc/{fdoc_id}/cancel
   * Allowed statuses: extracted, confirmed, extraction_failed. Blocks uploaded/extracting.
   * @param {string} fdoc_id - Document identifier
   * @param {Object} [params] - Optional body
   * @param {string} [params.reason] - Deletion reason (e.g. "file missing, re-uploading")
   * @returns {Promise<Object>} - Response with updated document (status: 'cancelled', etc.)
   */
  cancel: async (fdoc_id, params = {}) => {
    try {
      if (!fdoc_id) {
        throw new Error('fdoc_id is required');
      }

      const requestBody = { reason: params.reason || 'Deleted from frontend' };

      const responseData = await makeApiRequest(
        `/fdoc/${fdoc_id}/cancel`,
        'POST',
        requestBody
      );

      loggingService.logInfo(
        'api_success_fdoc',
        'Successfully cancelled document',
        { endpoint: `/fdoc/${fdoc_id}/cancel`, method: 'POST', fdocId: fdoc_id }
      ).catch(() => {});

      return responseData;
    } catch (error) {
      console.error('FDOC API: Error in cancel:', error);
      const msg = error.message || '';
      if (msg.includes('Failed to fetch') || msg.includes('Network error')) {
        throw new Error(
          'Delete failed due to CORS (preflight/OPTIONS) on `/fdoc/{fdoc_id}/cancel`. Backend must enable CORS for OPTIONS+POST on this route. (In dev, allow origin `http://localhost:3000`, headers `Authorization, Content-Type`).'
        );
      }
      if (error.status === 400) {
        throw new Error(error.responseData?.message || error.message || 'Bad Request');
      }
      if (error.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }
      if (error.status === 404) {
        throw new Error('Document not found.');
      }
      if (error.status === 500) {
        throw new Error(error.responseData?.error || 'Server error');
      }
      throw new Error(error.message || 'Error cancelling document');
    }
  },

  /**
   * List all documents for the authenticated user with optional filtering and pagination
   * GET /fdoc
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.status] - Filter by status: 'uploaded', 'extracting', 'extracted', 'extraction_failed', 'confirmed'
   * @param {string} [params.document_type] - Filter by upload type: 'bill', 'invoice', 'po', 'quotation', etc.
   * @param {string} [params.esign_status] - Filter by e-sign: 'applied', 'requested', 'none'
   * @param {string} [params.site_id] - Filter by site ID
   * @param {number} [params.limit] - Page size (default: 200, max: 500)
   * @param {string} [params.last_key] - JSON-encoded DynamoDB key for pagination
   * @returns {Promise<Object>} - Response object containing:
   *   - items: Array of document records
   *   - count: Number of documents returned
   *   - last_key: Pagination token for next page (null if last)
   * @throws {Error} - Throws an error if the API request fails
   * 
   * @example
   * // List all documents
   * const result = await fdocApi.listDocuments();
   * 
   * @example
   * // List only extracted documents
   * const result = await fdocApi.listDocuments({ status: 'extracted' });
   * 
   * @example
   * // List with pagination
   * const result = await fdocApi.listDocuments({ 
   *   limit: 50, 
   *   last_key: '...' 
   * });
   */
  listDocuments: async (params = {}) => {
    try {
      console.log('FDOC API: Listing documents', params);

      // Build query parameters
      const queryParams = [];

      if (params.status) {
        const validStatuses = [
          'uploaded',
          'extracting',
          'extracted',
          'extraction_failed',
          'confirmed',
        ];
        if (!validStatuses.includes(params.status)) {
          throw new Error(
            `status must be one of: ${validStatuses.join(', ')}`
          );
        }
        queryParams.push(`status=${encodeURIComponent(params.status)}`);
      }

      if (params.document_type) {
        queryParams.push(`document_type=${encodeURIComponent(params.document_type)}`);
      }

      if (params.esign_status) {
        const validEsignStatuses = ['applied', 'requested', 'none'];
        if (!validEsignStatuses.includes(params.esign_status)) {
          throw new Error(
            `esign_status must be one of: ${validEsignStatuses.join(', ')}`
          );
        }
        queryParams.push(`esign_status=${encodeURIComponent(params.esign_status)}`);
      }

      if (params.site_id) {
        queryParams.push(`site_id=${encodeURIComponent(params.site_id)}`);
      }

      // Proposed optional filters (safe pass-through; backend may ignore until implemented).
      appendOptionalQueryParam(queryParams, 'vendor_id', params.vendor_id);
      appendOptionalQueryParam(queryParams, 'customer_id', params.customer_id);
      appendOptionalQueryParam(queryParams, 'date_from', params.date_from);
      appendOptionalQueryParam(queryParams, 'date_to', params.date_to);

      if (params.limit !== undefined) {
        if (params.limit < 1 || params.limit > 500) {
          throw new Error('limit must be between 1 and 500');
        }
        queryParams.push(`limit=${params.limit}`);
      }

      if (params.last_key) {
        queryParams.push(`last_key=${encodeURIComponent(params.last_key)}`);
      }

      // Build endpoint with query parameters
      let endpoint = '/fdoc';
      if (queryParams.length > 0) {
        endpoint += `?${queryParams.join('&')}`;
      }

      const responseData = await makeApiRequest(endpoint);

      // Validate response structure
      if (responseData && typeof responseData === 'object') {
        // Log API event for successful list
        loggingService.logInfo(
          'api_success_fdoc',
          `Successfully listed documents`,
          {
            endpoint: '/fdoc',
            method: 'GET',
            status: params.status,
            count: responseData.count || 0,
          }
        ).catch(logError => {
          console.warn('[FdocApi] Failed to log success event:', logError);
        });
        
        return {
          items: Array.isArray(responseData.items) ? responseData.items : [],
          count: responseData.count || 0,
          last_key: responseData.last_key || null,
        };
      } else {
        throw new Error('Invalid response format from FDOC API');
      }
    } catch (error) {
      console.error('FDOC API: Error in listDocuments:', error);

      if (error.status === 400) {
        throw new Error(
          `Bad Request: ${error.responseData?.message || error.message}`
        );
      } else if (error.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (error.status === 500) {
        throw new Error(
          `Server error: ${error.responseData?.error || 'Unknown server error'}`
        );
      } else {
        throw new Error(`Error listing documents: ${error.message}`);
      }
    }
  },

  /**
   * Get vendor/customer filter options for FDOC order history.
   * GET /fdoc/order-history/filters
   */
  getOrderHistoryFilters: async (params = {}) => {
    try {
      const queryParams = [];
      const kind = params.kind || 'both';
      if (!['vendor', 'customer', 'both'].includes(kind)) {
        throw new Error('kind must be one of: vendor, customer, both');
      }
      queryParams.push(`kind=${encodeURIComponent(kind)}`);
      appendOptionalQueryParam(queryParams, 'date_from', params.date_from);
      appendOptionalQueryParam(queryParams, 'date_to', params.date_to);
      appendOptionalQueryParam(queryParams, 'status', params.status);
      appendOptionalQueryParam(queryParams, 'site_id', params.site_id);
      if (params.limit !== undefined) {
        if (params.limit < 1 || params.limit > 200) {
          throw new Error('limit must be between 1 and 200');
        }
        queryParams.push(`limit=${params.limit}`);
      }

      const endpoint = `/fdoc/order-history/filters${queryParams.length ? `?${queryParams.join('&')}` : ''}`;
      const responseData = await makeApiRequest(endpoint, 'GET', null, FDOC_ORDER_HISTORY_BASE_URL);

      return {
        kind: responseData?.kind || kind,
        date_from: responseData?.date_from || null,
        date_to: responseData?.date_to || null,
        count_source_items: responseData?.count_source_items || 0,
        vendors: Array.isArray(responseData?.vendors) ? responseData.vendors : [],
        customers: Array.isArray(responseData?.customers) ? responseData.customers : [],
        last_key: responseData?.last_key || null,
      };
    } catch (error) {
      throw new Error(`Error fetching order-history filters: ${error.message}`);
    }
  },

  /**
   * Get FDOC order history for vendor or customer view.
   * GET /fdoc/order-history
   */
  getOrderHistory: async (params = {}) => {
    try {
      if (!params.kind || !['vendor', 'customer'].includes(params.kind)) {
        throw new Error('kind is required and must be one of: vendor, customer');
      }

      const queryParams = [`kind=${encodeURIComponent(params.kind)}`];
      appendOptionalQueryParam(queryParams, 'vendor_name', params.vendor_name);
      appendOptionalQueryParam(queryParams, 'vendor_id', params.vendor_id);
      appendOptionalQueryParam(queryParams, 'customer_name', params.customer_name);
      appendOptionalQueryParam(queryParams, 'customer_id', params.customer_id);
      appendOptionalQueryParam(queryParams, 'date_from', params.date_from);
      appendOptionalQueryParam(queryParams, 'date_to', params.date_to);
      appendOptionalQueryParam(queryParams, 'status', params.status);
      appendOptionalQueryParam(queryParams, 'site_id', params.site_id);
      appendOptionalQueryParam(queryParams, 'last_key', params.last_key);
      if (params.limit !== undefined) {
        if (params.limit < 1 || params.limit > 200) {
          throw new Error('limit must be between 1 and 200');
        }
        queryParams.push(`limit=${params.limit}`);
      }

      const endpoint = `/fdoc/order-history?${queryParams.join('&')}`;
      const responseData = await makeApiRequest(endpoint, 'GET', null, FDOC_ORDER_HISTORY_BASE_URL);

      return {
        kind: responseData?.kind || params.kind,
        date_from: responseData?.date_from || null,
        date_to: responseData?.date_to || null,
        entries: Array.isArray(responseData?.entries) ? responseData.entries : [],
        count: responseData?.count || 0,
        summary: responseData?.summary || { total_amount_sum: 0, tax_total_sum: 0 },
        last_key: responseData?.last_key || null,
        applied_filters: responseData?.applied_filters || {},
      };
    } catch (error) {
      throw new Error(`Error fetching FDOC order history: ${error.message}`);
    }
  },
};

